/**
 * MapEditorPage
 *
 * Page wrapper for the GymMapEditor component.
 * Handles data fetching, permission checks, and routing.
 *
 * Features:
 * - Loads gym data and floor plan
 * - Checks user permissions (owner or setter)
 * - Passes data to GymMapEditor
 * - Handles navigation back to settings
 *
 * @example
 * ```tsx
 * // In router
 * <Route path="/gyms/:gymId/settings/floor-plan/editor" element={<MapEditorPage />} />
 * ```
 */

import { type CSSProperties } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import { useGym } from '@/hooks/useGyms';
import { useGymMemberships } from '@/hooks/useGymMemberships';
import { GymMapEditor } from '@/components/gym-map';
import type { WallSectionRegion } from '@flash/shared';

/**
 * MapEditorPage Component
 *
 * Wraps GymMapEditor with data fetching and permission checks.
 */
export const MapEditorPage = () => {
  const { gymId } = useParams<{ gymId: string }>();
  const navigate = useNavigate();

  // Fetch gym data
  const { gym, loading: gymLoading, error: gymError, refetch: refetchGym } = useGym(gymId);

  // Check user permissions
  const { getRoleAtGym, loading: membershipLoading } = useGymMemberships();
  const loading = gymLoading || membershipLoading;
  const userRole = gymId ? getRoleAtGym(gymId) : null;
  const canEdit = userRole === 'owner' || userRole === 'setter';

  /**
   * Handle close - navigate back to floor plan settings tab
   */
  const handleClose = () => {
    navigate(`/gyms/${gymId}/settings?tab=floorplan`);
  };

  /**
   * Handle save - update floor plan regions in Firestore
   */
  const handleSave = async (regions: WallSectionRegion[]): Promise<void> => {
    if (!gymId || !gym?.floorPlan) return;

    const db = getFirebaseDb();
    const gymRef = doc(db, 'gyms', gymId);

    await updateDoc(gymRef, {
      'floorPlan.regions': regions,
      updatedAt: Timestamp.now(),
    });

    // Refetch to get updated data
    refetchGym();
  };

  // Loading state
  if (loading) {
    return (
      <main style={containerStyle}>
        <div style={centerContainerStyle}>
          <p style={loadingTextStyle}>Loading editor...</p>
        </div>
      </main>
    );
  }

  // Error state
  if (gymError) {
    return (
      <main style={containerStyle}>
        <div style={centerContainerStyle}>
          <h2 style={errorTitleStyle}>Error Loading Gym</h2>
          <p style={errorTextStyle}>{gymError}</p>
          <button onClick={handleClose} style={buttonStyle}>
            Go Back
          </button>
        </div>
      </main>
    );
  }

  // Gym not found
  if (!gym) {
    return (
      <main style={containerStyle}>
        <div style={centerContainerStyle}>
          <h2 style={errorTitleStyle}>Gym Not Found</h2>
          <p style={errorTextStyle}>{"The gym you're looking for doesn't exist."}</p>
          <button onClick={() => navigate('/gyms')} style={buttonStyle}>
            View All Gyms
          </button>
        </div>
      </main>
    );
  }

  // No permission
  if (!canEdit) {
    return (
      <main style={containerStyle}>
        <div style={centerContainerStyle}>
          <h2 style={errorTitleStyle}>Access Denied</h2>
          <p style={errorTextStyle}>Only gym owners and setters can edit floor plan regions.</p>
          <button onClick={handleClose} style={buttonStyle}>
            Go Back
          </button>
        </div>
      </main>
    );
  }

  // No floor plan configured
  if (!gym.floorPlan) {
    return (
      <main style={containerStyle}>
        <div style={centerContainerStyle}>
          <h2 style={errorTitleStyle}>No Floor Plan</h2>
          <p style={errorTextStyle}>
            Please upload a floor plan image in gym settings before editing regions.
          </p>
          <button onClick={handleClose} style={buttonStyle}>
            Go to Settings
          </button>
        </div>
      </main>
    );
  }

  // Render the editor
  return <GymMapEditor floorPlan={gym.floorPlan} onSave={handleSave} onClose={handleClose} />;
};

/**
 * Component styles
 */
const containerStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'var(--color-bg-primary, #ffffff)',
  zIndex: 1000,
};

const centerContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
  textAlign: 'center',
};

const loadingTextStyle: CSSProperties = {
  fontSize: '1.125rem',
  color: 'var(--color-text-secondary, #6b7280)',
};

const errorTitleStyle: CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 600,
  color: 'var(--color-text-primary, #1f2937)',
  marginBottom: '8px',
};

const errorTextStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-secondary, #6b7280)',
  marginBottom: '16px',
};

const buttonStyle: CSSProperties = {
  padding: '12px 24px',
  backgroundColor: 'var(--color-primary, #3b82f6)',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 500,
  cursor: 'pointer',
};

export default MapEditorPage;
